import { EntityKind } from "../net/connection";

export const MOB_COLORS_URL = "creaturemesh/colors.json";

/** The texture factor `BeginModulateRender` multiplies a body by
 *  (`ActorInstanceRender.cpp:253-270`): the race's `MOB_COLOR` as
 *  `D3DXCOLOR` reads a DWORD (`InstanceBase.cpp:821-828`). A race with no
 *  colour draws in `RENDER_MODE_NORMAL`, and a body mid-fade draws in
 *  `RENDER_MODE_BLEND`, whose pass has no factor (`:171-184`); both are white. */
export function modulateColour(color: number, alpha: number): [number, number, number] {
  if (color === 0 || alpha < 1) return [1, 1, 1];
  return [((color >>> 16) & 0xff) / 255, ((color >>> 8) & 0xff) / 255, (color & 0xff) / 255];
}

/** `colors.json`'s rows by monster vnum, as `tools/build_mob_colors.py`
 *  writes them, or an empty table for a body that holds none. */
export function mobColorTable(body: unknown): ReadonlyMap<number, number> {
  const vnums = (body as { vnums?: Record<string, number> } | null)?.vnums;
  return new Map(Object.entries(vnums ?? {}).map(([vnum, color]) => [Number(vnum), color]));
}

/** The `MOB_COLOR` a body is created with: its race's, and none for a player,
 *  polymorphed or not, whose instance type stays `TYPE_PC`
 *  (`InstanceBase.cpp:821`, `ActorInstance.cpp:282-288`, `char.cpp:808`). */
export function mobColorOf(table: ReadonlyMap<number, number>, kind: number, mobVnum: number): number {
  return kind === EntityKind.PLAYER ? 0 : table.get(mobVnum) ?? 0;
}

/** The table by species name, or an empty one when the file is absent or
 *  unreadable: a body without its colour is still drawn. */
export async function loadMobColors(
  fetchJson: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<ReadonlyMap<number, number>> {
  try {
    const response = await fetchJson(MOB_COLORS_URL);
    if (!response.ok) return new Map();
    return mobColorTable(await response.json());
  } catch {
    return new Map();
  }
}
